import { TechnologyType } from '@@types/api.types';
import type { SubsystemCreateDto } from '@@types/api.types';
import {
  LONG_DESCRIPTION_MAX_LENGTH,
  LONG_DESCRIPTION_VALIDATION_MESSAGES,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zArrayWithoutEmptyObjectSchema, zCoercedOptional, zDecimalNumber, zRequiredUuid } from '@utils/zod.util';
import { z } from 'zod';

/**
 * Schema for validating component data in subsystems.
 * Accepts price as string and transforms it to number for API compatibility.
 * Ensures name is provided and price is a valid positive number if specified.
 */
const componentCreateSchema = z
  .object({
    id: z.string().uuid().optional(),
    name: z
      .string()
      .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
      .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
    origin: zCoercedOptional(z.string().optional()),
    price: zDecimalNumber,
  })
  .strip();

/**
 * Schema for validating subsystem creation data for API submission.
 * Converts form data to proper API format with numeric prices.
 * Also used for updates with an additional id field.
 */
export const subsystemCreateSchema = z
  .object({
    categoryId: zRequiredUuid('Please select a category'),
    components: zArrayWithoutEmptyObjectSchema(componentCreateSchema),
    description: zCoercedOptional(
      z.string().max(LONG_DESCRIPTION_MAX_LENGTH, { message: LONG_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
    ),
    id: z.string().uuid().optional(),
    name: z
      .string()
      .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
      .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
    origin: zCoercedOptional(z.string().optional()),
    subcategoryId: zRequiredUuid('Please select a subcategory'),
    technologyId: zRequiredUuid('Technology ID is required'),
    type: z.nativeEnum(TechnologyType, {
      errorMap: () => ({ message: 'Please select a subsystem type' }),
    }),
  })
  .strip() satisfies z.ZodType<SubsystemCreateDto>;

export type SubsystemFormData = z.infer<typeof subsystemCreateSchema>;
export type SubsystemFormErrors = z.inferFormattedError<typeof subsystemCreateSchema, string>;
